// Popup de una acción del plan de crisis: todo lo que hace falta para resolverla.
//
// Cuatro bloques:
//   · Paso a paso — lo arma la IA a pedido (qué hacer, en qué orden, qué
//     preparar, a quién buscar). Se cachea.
//   · Contactos — teléfonos y emails REALES que carga el equipo. La IA sugiere
//     a quién buscar; el dato lo pone quien lo tiene. Nunca se inventa.
//   · Consultar a la IA — chat por acción, para pedir el borrador o preguntar.
//   · Estado y borrar.

import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import {
  FaTimes, FaCheck, FaRegCircle, FaSpinner, FaTrash, FaPlus, FaMagic,
  FaPaperPlane, FaRobot, FaUser, FaRegCopy, FaPhone, FaEnvelope, FaRedo,
  FaFileAlt, FaPen, FaSearch,
} from 'react-icons/fa';
import {
  TechInput, TechButton, TechButtonGhost, TechButtonDanger, TechChip,
  mono, borderSoft,
} from '../../styles/techComponents';
import {
  chatAccion, getPlanAccion, actualizarAccion, buscarContactos,
  crearContacto, actualizarContacto, borrarContacto,
} from '../../services/crisisService';

const OK = '#06d6a0';
const TIBIO = '#ffd166';

const Fondo = styled.div`
  position: fixed; inset: 0;
  background: rgba(11, 19, 32, 0.85);
  backdrop-filter: blur(4px);
  display: flex; align-items: center; justify-content: center;
  z-index: 1000; padding: 1rem;
`;

const Caja = styled.div`
  background: var(--panel-bg, #0f172a);
  border: 1px solid ${borderSoft};
  border-radius: 12px;
  max-width: 760px; width: 100%; max-height: 90vh;
  display: flex; flex-direction: column;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.5);
`;

const Cabecera = styled.div`
  display: flex; align-items: flex-start; justify-content: space-between; gap: 12px;
  padding: 1rem 1.3rem; border-bottom: 1px solid ${borderSoft};
`;

const TituloBox = styled.div` flex: 1; min-width: 0; `;

const Titulo = styled.h3`
  margin: 0 0 6px; color: var(--text-primary);
  font-size: 1.02rem; font-weight: 600; line-height: 1.4;
`;

const Chips = styled.div` display: flex; gap: 6px; flex-wrap: wrap; align-items: center; `;

const EstadoBtn = styled.button`
  display: inline-flex; align-items: center; gap: 5px;
  font-size: 0.72rem; font-family: ${mono};
  padding: 3px 9px; border-radius: 4px; cursor: pointer;
  color: ${(p) => (p.$activo ? '#0b1320' : 'var(--text-secondary)')};
  background: ${(p) => (p.$activo ? p.$c : 'transparent')};
  border: 1px solid ${(p) => (p.$activo ? p.$c : borderSoft)};
  &:hover { border-color: ${(p) => p.$c}; }
`;

const Responsable = styled.div`
  margin-top: 8px; display: flex; gap: 8px; align-items: center; flex-wrap: wrap;
  .link {
    background: none; border: none; cursor: pointer; padding: 0;
    color: var(--btn-primary); font-size: 0.8rem; font-family: inherit;
    display: inline-flex; align-items: center; gap: 5px;
  }
  .link:hover { text-decoration: underline; }
`;

const Cerrar = styled.button`
  background: none; border: none; color: var(--text-secondary); cursor: pointer;
  font-size: 1rem; padding: 4px; display: flex; flex-shrink: 0; border-radius: 4px;
  &:hover { color: var(--text-primary); background: rgba(255, 255, 255, 0.06); }
`;

const Cuerpo = styled.div` overflow-y: auto; padding: 0 1.3rem 1.2rem; `;

const Seccion = styled.section`
  padding: 16px 0; border-bottom: 1px solid ${borderSoft};
  &:last-child { border-bottom: none; }
`;

const Encabe = styled.div`
  display: flex; align-items: center; justify-content: space-between; gap: 10px;
  margin-bottom: 12px;
`;

const HTitulo = styled.h4`
  margin: 0; font-size: 0.72rem; letter-spacing: 0.14em; text-transform: uppercase;
  color: var(--text-secondary); font-family: ${mono};
  display: flex; align-items: center; gap: 7px;
`;

const Detalle = styled.p`
  margin: 0; font-size: 0.86rem; line-height: 1.6; color: var(--text-secondary);
`;

const Resumen = styled.p`
  margin: 0 0 12px; font-size: 0.88rem; line-height: 1.6; color: var(--text-primary);
  padding-left: 10px; border-left: 2px solid var(--btn-primary);
`;

// --- Pasos ---
const Paso = styled.div`
  display: flex; gap: 11px; padding: 9px 0;
  border-bottom: 1px dashed ${borderSoft};
  &:last-child { border-bottom: none; }
  .n {
    flex-shrink: 0; width: 22px; height: 22px; border-radius: 50%;
    display: flex; align-items: center; justify-content: center;
    font-family: ${mono}; font-size: 0.72rem; font-weight: 600;
    color: var(--btn-primary); border: 1px solid rgba(0, 180, 216, 0.4);
    background: rgba(0, 180, 216, 0.08);
  }
  .txt { flex: 1; min-width: 0; }
  .q { font-size: 0.88rem; color: var(--text-primary); line-height: 1.5; }
  .c { font-size: 0.8rem; color: var(--text-secondary); margin-top: 3px; line-height: 1.5; }
  .m {
    font-size: 0.72rem; color: var(--text-secondary); margin-top: 4px;
    display: flex; gap: 10px; flex-wrap: wrap; font-family: ${mono};
  }
`;

const Listita = styled.ul`
  margin: 6px 0 0; padding-left: 18px;
  li { font-size: 0.82rem; color: var(--text-secondary); line-height: 1.6; }
`;

// --- Contactos ---
const Contacto = styled.div`
  display: flex; align-items: flex-start; gap: 10px; padding: 9px 0;
  border-bottom: 1px solid ${borderSoft};
  &:last-child { border-bottom: none; }
  .info { flex: 1; min-width: 0; }
  .nom { font-size: 0.88rem; color: var(--text-primary); font-weight: 500; }
  .rol { font-size: 0.76rem; color: var(--text-secondary); margin-top: 2px; }
  .datos {
    display: flex; gap: 14px; flex-wrap: wrap; margin-top: 5px;
    font-size: 0.8rem;
  }
  a { color: var(--btn-primary); text-decoration: none; display: inline-flex; align-items: center; gap: 5px; }
  a:hover { text-decoration: underline; }
`;

const FormContacto = styled.div`
  display: grid; grid-template-columns: 1fr 1fr; gap: 8px; margin-top: 12px;
  padding: 12px; border: 1px dashed ${borderSoft}; border-radius: 8px;
  .full { grid-column: 1 / -1; }
  @media (max-width: 520px) { grid-template-columns: 1fr; .full { grid-column: 1; } }
`;

// --- Chat ---
const Hilo = styled.div`
  display: flex; flex-direction: column; gap: 10px; max-height: 340px; overflow-y: auto;
  padding: 12px; border: 1px solid ${borderSoft}; border-radius: 8px;
  background: rgba(0, 0, 0, 0.18); margin-bottom: 10px;
`;

const Burbuja = styled.div`
  display: flex; gap: 9px; align-items: flex-start;
  .icono { flex-shrink: 0; margin-top: 2px; font-size: 0.72rem;
    color: ${(p) => (p.$ia ? 'var(--btn-primary)' : 'var(--text-secondary)')}; }
  .texto { font-size: 0.85rem; line-height: 1.6; white-space: pre-wrap; min-width: 0; word-break: break-word;
    color: ${(p) => (p.$ia ? 'var(--text-primary)' : 'var(--text-secondary)')}; }
`;

const Atajos = styled.div` display: flex; gap: 6px; flex-wrap: wrap; margin-bottom: 8px; `;
const Atajo = styled.button`
  font-family: ${mono}; font-size: 0.68rem; color: var(--text-secondary);
  background: rgba(255, 255, 255, 0.04); border: 1px solid ${borderSoft};
  border-radius: 4px; padding: 4px 8px; cursor: pointer;
  &:hover { color: var(--btn-primary); border-color: rgba(0, 180, 216, 0.4); }
  &:disabled { opacity: 0.5; }
`;
const Escribir = styled.div` display: flex; gap: 8px; flex-wrap: wrap; > input { flex: 1; min-width: 200px; } `;
const Pensando = styled.div`
  font-size: 0.8rem; color: var(--text-secondary); display: flex; align-items: center; gap: 8px;
  svg { animation: girar 1s linear infinite; } @keyframes girar { to { transform: rotate(360deg); } }
`;
const Vacio = styled.p` margin: 0; font-size: 0.83rem; color: var(--text-secondary); line-height: 1.55; `;

const ATAJOS = [
  'Afiná el paso a paso para que sea más concreto',
  'Agregá los contactos que falten',
  'Escribime el borrador listo para usar',
  '¿Qué riesgo tiene?',
];

export default function AccionModal({ crisisId, accion, onCerrar, onCambio }) {
  const cajaRef = useRef(null);
  const vivoRef = useRef(true); // evita setState tras desmontar (el plan tarda hasta 120s)

  // `undefined` = cargando; `null` = falló; objeto = plan listo. La distinción
  // importa: sin ella, mientras carga un plan cacheado se mostraba el error
  // "no se pudo armar", que en crisis se lee como que el sistema se rompió.
  const [plan, setPlan] = useState(undefined);
  const [armando, setArmando] = useState(false);
  const [contactos, setContactos] = useState(accion.contactos || []);
  const [nuevoC, setNuevoC] = useState({ nombre: '', rol: '', telefono: '', email: '' });
  const [guardandoC, setGuardandoC] = useState(false);
  const [editandoC, setEditandoC] = useState(null); // id del contacto en edición
  const [buscandoTel, setBuscandoTel] = useState(false);
  const [resultadoBusqueda, setResultadoBusqueda] = useState(null);
  const [chat, setChat] = useState(accion.chat || []);
  const [mensaje, setMensaje] = useState('');
  const [pensando, setPensando] = useState(false);
  const [error, setError] = useState(null);
  const [copiado, setCopiado] = useState(null);
  const [ultimoCambio, setUltimoCambio] = useState(null);
  // Estado y responsable viven en local para que la cabecera se actualice al
  // cambiarlos sin re-abrir el modal (la prop `accion` es un snapshot).
  const [estado, setEstado] = useState(accion.estado);
  const [responsable, setResponsable] = useState(accion.responsable || '');
  const [editaResp, setEditaResp] = useState(false);

  useEffect(() => {
    vivoRef.current = true;
    const alTecla = (e) => { if (e.key === 'Escape') onCerrar(); };
    document.addEventListener('keydown', alTecla);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    cajaRef.current?.focus();
    return () => {
      vivoRef.current = false;
      document.removeEventListener('keydown', alTecla);
      document.body.style.overflow = prev;
    };
  }, [onCerrar]);

  const armarPlan = async (regenerar = false) => {
    setArmando(true); setError(null);
    try {
      const r = await getPlanAccion(crisisId, accion.id, regenerar);
      if (!vivoRef.current) return;
      setPlan(r.plan);
      if (r.contactos) setContactos(r.contactos);
      onCambio?.();
    } catch {
      if (!vivoRef.current) return;
      setError('No se pudo armar el paso a paso. Reintentá.');
      setPlan(null);
    } finally {
      if (vivoRef.current) setArmando(false);
    }
  };

  // Al abrir la acción: si ya tiene plan, se trae (cacheado); si no, se ARMA
  // SOLO. No hay botón — la acción llega lista para trabajar.
  useEffect(() => {
    if (accion.tiene_plan) {
      setArmando(true);
      getPlanAccion(crisisId, accion.id, false)
        .then((r) => {
          if (!vivoRef.current) return;
          setPlan(r.plan);
          if (r.contactos) setContactos(r.contactos);
        })
        .catch(() => { if (vivoRef.current) setPlan(null); })
        .finally(() => { if (vivoRef.current) setArmando(false); });
    } else {
      armarPlan(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [crisisId, accion.id]);

  const cambiarEstado = async (nuevo) => {
    const previo = estado;
    setEstado(nuevo); // optimista
    try {
      await actualizarAccion(crisisId, accion.id, { estado: nuevo });
      onCambio?.();
    } catch {
      setEstado(previo);
      setError('No se pudo cambiar el estado.');
    }
  };

  const guardarResponsable = async () => {
    setEditaResp(false);
    try {
      await actualizarAccion(crisisId, accion.id, { responsable: responsable.trim() });
      onCambio?.();
    } catch { setError('No se pudo guardar el responsable.'); }
  };

  const agregarContacto = async () => {
    if (!nuevoC.nombre.trim() || guardandoC) return;
    setGuardandoC(true);
    try {
      const r = await crearContacto(crisisId, accion.id, nuevoC);
      if (!vivoRef.current) return;
      setContactos(r.contactos);
      setNuevoC({ nombre: '', rol: '', telefono: '', email: '' });
      onCambio?.();
    } catch {
      setError('No se pudo guardar el contacto.');
    } finally {
      if (vivoRef.current) setGuardandoC(false);
    }
  };

  const guardarEdicionContacto = async (cid, cambios) => {
    try {
      const r = await actualizarContacto(crisisId, accion.id, cid, cambios);
      setContactos(r.contactos);
      setEditandoC(null);
      onCambio?.();
    } catch { setError('No se pudo actualizar el contacto.'); }
  };

  const quitarContacto = async (cid) => {
    try {
      const r = await borrarContacto(crisisId, accion.id, cid);
      setContactos(r.contactos);
      onCambio?.();
    } catch { setError('No se pudo borrar el contacto.'); }
  };

  const buscarTelefonos = async () => {
    if (buscandoTel) return;
    setBuscandoTel(true); setError(null); setResultadoBusqueda(null);
    try {
      const r = await buscarContactos(crisisId, accion.id);
      if (!vivoRef.current) return;
      setContactos(r.contactos);
      setResultadoBusqueda(
        r.encontrados > 0
          ? `Encontré datos de ${r.encontrados} contacto(s). Están marcados "verificar" con la fuente — confirmalos antes de usarlos.`
          : 'No encontré teléfonos confiables en la web. Cargalos a mano o probá dictándoselos al chat.',
      );
      onCambio?.();
    } catch (e) {
      setError(e?.response?.data?.error || 'No se pudo buscar en la web. Probá de nuevo.');
    } finally {
      if (vivoRef.current) setBuscandoTel(false);
    }
  };

  const preguntar = async (texto) => {
    const q = (texto ?? mensaje).trim();
    if (q.length < 2 || pensando) return;
    setPensando(true); setError(null);
    setChat((c) => [...c, { role: 'user', content: q }]);
    setMensaje('');
    try {
      const r = await chatAccion(crisisId, accion.id, q);
      setChat(r.chat || []);
      // El chat puede tocar el paso a paso y los contactos: se reflejan al toque.
      if (r.plan !== undefined) setPlan(r.plan);
      if (r.contactos) setContactos(r.contactos);
      setUltimoCambio(r.cambios?.length ? r.cambios.join(' y ') : null);
      onCambio?.();
    } catch {
      setError('No se pudo consultar. Probá de nuevo.');
      setChat((c) => c.slice(0, -1));
    } finally {
      setPensando(false);
    }
  };

  const copiar = async (t, i) => {
    try { await navigator.clipboard.writeText(t); setCopiado(i); setTimeout(() => setCopiado(null), 1800); }
    catch { /* sin portapapeles */ }
  };

  const ESTADOS_UI = [
    { k: 'pendiente', t: 'Sin empezar', c: 'var(--text-secondary)' },
    { k: 'en_curso', t: 'En curso', c: TIBIO },
    { k: 'hecha', t: 'Hecha', c: OK },
  ];

  return (
    <Fondo onClick={onCerrar} role="dialog" aria-modal="true" aria-label={accion.titulo}>
      <Caja ref={cajaRef} tabIndex={-1} onClick={(e) => e.stopPropagation()}>
        <Cabecera>
          <TituloBox>
            <Titulo>{accion.titulo}</Titulo>
            <Chips>
              <TechChip tone={accion.prioridad === 'alta' ? 'danger' : undefined}>
                {accion.prioridad}
              </TechChip>
              <TechChip>{accion.origen === 'kit' ? 'del plan' : 'a mano'}</TechChip>
              {/* Cambiar el estado sin salir del modal — el motivo por el que se
                  abre la acción es trabajarla, y marcarla debería ser de acá. */}
              {ESTADOS_UI.map((e) => (
                <EstadoBtn
                  key={e.k}
                  $activo={estado === e.k}
                  $c={e.c}
                  onClick={() => cambiarEstado(e.k)}
                >
                  {estado === e.k && (e.k === 'hecha' ? <FaCheck /> : e.k === 'en_curso' ? <FaSpinner /> : <FaRegCircle />)}
                  {e.t}
                </EstadoBtn>
              ))}
            </Chips>
            <Responsable>
              {editaResp ? (
                <>
                  <TechInput
                    autoFocus placeholder="A cargo de…"
                    value={responsable}
                    onChange={(ev) => setResponsable(ev.target.value)}
                    onKeyDown={(ev) => { if (ev.key === 'Enter') guardarResponsable(); }}
                    style={{ maxWidth: 220 }}
                  />
                  <TechButtonGhost onClick={guardarResponsable}>Guardar</TechButtonGhost>
                </>
              ) : (
                <button type="button" className="link" onClick={() => setEditaResp(true)}>
                  <FaUser style={{ fontSize: '0.68rem' }} />{' '}
                  {responsable ? `A cargo: ${responsable}` : 'Asignar responsable'}
                </button>
              )}
            </Responsable>
          </TituloBox>
          <Cerrar onClick={onCerrar} aria-label="Cerrar"><FaTimes /></Cerrar>
        </Cabecera>

        <Cuerpo>
          {accion.detalle && (
            <Seccion>
              <Detalle>{accion.detalle}</Detalle>
            </Seccion>
          )}

          {error && <Detalle style={{ color: 'var(--alert-error)', paddingTop: 10 }}>{error}</Detalle>}

          {/* ---------- Paso a paso ---------- */}
          <Seccion>
            <Encabe>
              <HTitulo><FaMagic /> Paso a paso</HTitulo>
              {plan && (
                <TechButtonGhost onClick={() => armarPlan(true)} disabled={armando}>
                  <FaRedo /> Rehacer
                </TechButtonGhost>
              )}
            </Encabe>

            {armando || plan === undefined ? (
              <Pensando><FaSpinner /> Armando el paso a paso y los contactos…</Pensando>
            ) : plan === null ? (
              <>
                <Vacio>No se pudo armar el paso a paso.</Vacio>
                <div style={{ marginTop: 10 }}>
                  <TechButton onClick={() => armarPlan(false)}>
                    <FaMagic /> Reintentar
                  </TechButton>
                </div>
              </>
            ) : (
              <>
                {plan.resumen && <Resumen>{plan.resumen}</Resumen>}
                {plan.pasos.map((s) => (
                  <Paso key={s.orden}>
                    <span className="n">{s.orden}</span>
                    <div className="txt">
                      <div className="q">{s.que}</div>
                      {s.como && <div className="c">{s.como}</div>}
                      {(s.quien || s.plazo) && (
                        <div className="m">
                          {s.quien && <span>👤 {s.quien}</span>}
                          {s.plazo && <span>🕐 {s.plazo}</span>}
                        </div>
                      )}
                    </div>
                  </Paso>
                ))}

                {plan.documentos?.length > 0 && (
                  <div style={{ marginTop: 12 }}>
                    <HTitulo style={{ marginBottom: 6 }}><FaFileAlt /> Qué tener a mano</HTitulo>
                    <Listita>{plan.documentos.map((d, i) => <li key={i}>{d}</li>)}</Listita>
                  </div>
                )}
                {plan.plazo_total && (
                  <div style={{ marginTop: 10, fontSize: '0.78rem', color: 'var(--text-secondary)', fontFamily: mono }}>
                    Plazo total sugerido: {plan.plazo_total}
                  </div>
                )}
              </>
            )}
          </Seccion>

          {/* ---------- Contactos ---------- */}
          <Seccion>
            <Encabe>
              <HTitulo><FaPhone /> Contactos</HTitulo>
              {contactos.some((c) => !c.telefono && !c.email) && (
                <TechButton onClick={buscarTelefonos} disabled={buscandoTel}>
                  {buscandoTel ? <>Buscando en la web…</> : <><FaSearch /> Buscar teléfonos en la web</>}
                </TechButton>
              )}
            </Encabe>
            <Vacio style={{ marginBottom: 12 }}>
              La IA arma la lista de a quién llamar. Con "Buscar teléfonos" los rastrea en la web
              y los trae con la fuente para que verifiques (los directorios pueden estar viejos).
              También los podés cargar a mano o dictárselos al chat. La IA nunca inventa un número:
              lo que trae, sale de una búsqueda real.
            </Vacio>

            {resultadoBusqueda && (
              <div style={{
                marginBottom: 12, fontSize: '0.82rem', lineHeight: 1.5,
                color: 'var(--text-primary)', padding: '8px 10px',
                border: '1px solid rgba(0,180,216,0.3)', borderRadius: 6,
                background: 'rgba(0,180,216,0.06)',
              }}>
                {resultadoBusqueda}
              </div>
            )}

            {contactos.length === 0 ? (
              <Vacio>Todavía no hay contactos.</Vacio>
            ) : (
              contactos.map((c) => (editandoC === c.id ? (
                <ContactoEdicion
                  key={c.id}
                  contacto={c}
                  onGuardar={(cambios) => guardarEdicionContacto(c.id, cambios)}
                  onCancelar={() => setEditandoC(null)}
                />
              ) : (
                <Contacto key={c.id}>
                  <div className="info">
                    <div className="nom">{c.nombre}</div>
                    {c.rol && <div className="rol">{c.rol}</div>}
                    <div className="datos">
                      {c.telefono
                        ? <a href={`tel:${c.telefono}`}><FaPhone style={{ fontSize: '0.7rem' }} /> {c.telefono}</a>
                        : <span style={{ color: 'var(--text-secondary)', opacity: 0.6 }}>sin teléfono</span>}
                      {c.email && <a href={`mailto:${c.email}`}><FaEnvelope style={{ fontSize: '0.7rem' }} /> {c.email}</a>}
                    </div>
                    {c.nota && <div className="rol" style={{ marginTop: 4 }}>{c.nota}</div>}
                  </div>
                  <div style={{ display: 'flex', gap: 6 }}>
                    <TechButtonGhost onClick={() => setEditandoC(c.id)} title="Editar contacto">
                      <FaPen />
                    </TechButtonGhost>
                    <TechButtonDanger onClick={() => quitarContacto(c.id)} title="Borrar contacto">
                      <FaTrash />
                    </TechButtonDanger>
                  </div>
                </Contacto>
              )))
            )}

            <FormContacto>
              <TechInput placeholder="Nombre o institución"
                value={nuevoC.nombre} onChange={(e) => setNuevoC({ ...nuevoC, nombre: e.target.value })} />
              <TechInput placeholder="Rol (abogado, editor…)"
                value={nuevoC.rol} onChange={(e) => setNuevoC({ ...nuevoC, rol: e.target.value })} />
              <TechInput placeholder="Teléfono"
                value={nuevoC.telefono} onChange={(e) => setNuevoC({ ...nuevoC, telefono: e.target.value })} />
              <TechInput placeholder="Email"
                value={nuevoC.email} onChange={(e) => setNuevoC({ ...nuevoC, email: e.target.value })} />
              <div className="full">
                <TechButton onClick={agregarContacto} disabled={!nuevoC.nombre.trim() || guardandoC}>
                  <FaPlus /> {guardandoC ? 'Guardando…' : 'Agregar contacto'}
                </TechButton>
              </div>
            </FormContacto>
          </Seccion>

          {/* ---------- Chat ---------- */}
          <Seccion>
            <HTitulo style={{ marginBottom: 8 }}><FaRobot /> Afinar con la IA</HTitulo>
            <Vacio style={{ marginBottom: 12 }}>
              Pedile cambios y los hace: "sacá el paso 3", "agregá al Colegio de Escribanos",
              "el teléfono de la fiscalía es 336-4455667". También te escribe el borrador o te
              dice cómo seguir.
            </Vacio>

            {ultimoCambio && (
              <div style={{
                marginBottom: 10, fontSize: '0.8rem', color: OK,
                display: 'flex', alignItems: 'center', gap: 7,
              }}>
                <FaCheck /> La IA actualizó {ultimoCambio} de esta acción.
              </div>
            )}

            {chat.length > 0 && (
              <Hilo>
                {chat.map((m, i) => (
                  <Burbuja key={i} $ia={m.role === 'assistant'}>
                    <span className="icono">{m.role === 'assistant' ? <FaRobot /> : <FaUser />}</span>
                    <div className="texto">
                      {m.content}
                      {m.role === 'assistant' && (
                        <div style={{ marginTop: 6 }}>
                          <Atajo onClick={() => copiar(m.content, i)}>
                            <FaRegCopy /> {copiado === i ? 'copiado' : 'copiar'}
                          </Atajo>
                        </div>
                      )}
                    </div>
                  </Burbuja>
                ))}
                {pensando && <Pensando><FaSpinner /> Pensando…</Pensando>}
              </Hilo>
            )}

            <Atajos>
              {ATAJOS.map((a) => <Atajo key={a} onClick={() => preguntar(a)} disabled={pensando}>{a}</Atajo>)}
            </Atajos>
            <Escribir>
              <TechInput placeholder="Preguntá lo que necesites…"
                value={mensaje} onChange={(e) => setMensaje(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') preguntar(); }} disabled={pensando} />
              <TechButton onClick={() => preguntar()} disabled={pensando || mensaje.trim().length < 2}>
                <FaPaperPlane /> Preguntar
              </TechButton>
            </Escribir>
          </Seccion>
        </Cuerpo>
      </Caja>
    </Fondo>
  );
}

// Edición inline de un contacto ya cargado (corregir un dígito del teléfono sin
// borrar y recrear).
function ContactoEdicion({ contacto, onGuardar, onCancelar }) {
  const [f, setF] = useState({
    nombre: contacto.nombre || '', rol: contacto.rol || '',
    telefono: contacto.telefono || '', email: contacto.email || '',
  });
  return (
    <FormContacto style={{ marginTop: 0, marginBottom: 10 }}>
      <TechInput placeholder="Nombre" value={f.nombre}
        onChange={(e) => setF({ ...f, nombre: e.target.value })} />
      <TechInput placeholder="Rol" value={f.rol}
        onChange={(e) => setF({ ...f, rol: e.target.value })} />
      <TechInput placeholder="Teléfono" value={f.telefono}
        onChange={(e) => setF({ ...f, telefono: e.target.value })} />
      <TechInput placeholder="Email" value={f.email}
        onChange={(e) => setF({ ...f, email: e.target.value })} />
      <div className="full" style={{ display: 'flex', gap: 8 }}>
        <TechButton onClick={() => onGuardar(f)} disabled={!f.nombre.trim()}>Guardar</TechButton>
        <TechButtonGhost onClick={onCancelar}>Cancelar</TechButtonGhost>
      </div>
    </FormContacto>
  );
}
